import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { userAPI, roleAPI, profileAPI, dataRoleAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { formatDateTime } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { OrgChartView } from '../components/OrgChartView';
import { ProductHierarchyTab } from '../components/ProductHierarchyTab';
import { HierarchyDriftPanel } from '../components/HierarchyDriftPanel';
import { HierarchySyncPanel } from '../components/HierarchySyncPanel';
import { toast } from 'sonner';
import { Loader2, Users, Pencil, UserX, UserCheck, Plus, Trash2, Shield, Search, ChevronDown, ChevronRight, Check, X as XIcon, KeyRound, LockOpen } from 'lucide-react';
import { Input } from '../components/ui/input';

const PAGE_SIZE = 50;

// 25 Oct 2026 (Security Round 2A, Task 3c): mirrors backend/deps.py's PROFILE_TO_LEGACY_ROLE
// exactly — keep the two in sync if that dict ever changes. Custom (non-system) profiles are
// intentionally absent and fall back to leaving the role list untouched (filtered of admin
// roles only), same as before.
const PROFILE_TO_LEGACY_ROLE = {
  super_admin_profile: 'super_admin',
  admin_profile: 'admin',
  pm_profile: 'product_manager',
  product_executive_profile: 'product_executive',
  buyer_profile: 'buyer',
  sales_profile: 'sales',
  approver_profile: 'manager',
  readonly_profile: 'viewer',
  accounts_profile: 'accounts',
  reviewer_ea_profile: 'viewer',
  hr_manager_profile: 'viewer',
  hr_profile: 'viewer',
  trainer_profile: 'viewer',
  staff_profile: 'viewer',
};

// 25 Oct 2026 (Security Round 2A, Task 3d): mirrors backend/deps.py's is_admin_level() —
// same three conditions, computed from fields already in UserResponse (roles, profile_id,
// data_scope) so the list and the "Admin-level only" filter don't need a new endpoint.
const ADMIN_LEVEL_LEGACY_ROLES = ['super_admin', 'admin', 'product_admin'];
const ADMIN_LEVEL_PROFILES = ['super_admin_profile', 'admin_profile'];
const isUserAdminLevel = (u) =>
  ADMIN_LEVEL_LEGACY_ROLES.some(r => (u.roles || []).includes(r)) ||
  ADMIN_LEVEL_PROFILES.includes(u.profile_id) ||
  u.data_scope === 'ALL';

export default function UsersPage() {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState(searchParams.get('q') || '');
  const [debouncedSearch, setDebouncedSearch] = useState(searchParams.get('q') || '');
  const [adminLevelOnly, setAdminLevelOnly] = useState(searchParams.get('admin_only') === '1');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'all');
  const [profileFilter, setProfileFilter] = useState(searchParams.get('profile_id') || 'all');
  const [dataRoleFilter, setDataRoleFilter] = useState(searchParams.get('data_role_id') || 'all');
  const [zohoRoleFilter, setZohoRoleFilter] = useState(searchParams.get('zoho_role') || 'all');
  const [reportsToId, setReportsToId] = useState(searchParams.get('reports_to') || 'all');
  const [reportsToName, setReportsToName] = useState(searchParams.get('reports_to_name') || '');
  const [allLevels, setAllLevels] = useState(searchParams.get('all_levels') === '1');
  const [zohoRoleOptions, setZohoRoleOptions] = useState([]);
  const [mgrQuery, setMgrQuery] = useState('');
  const [mgrResults, setMgrResults] = useState([]);
  const [mgrPickerOpen, setMgrPickerOpen] = useState(false);
  const [roles, setRoles] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [dataRoles, setDataRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [manualMgrQuery, setManualMgrQuery] = useState('');
  const [manualMgrResults, setManualMgrResults] = useState([]);
  const [manualMgrSaving, setManualMgrSaving] = useState(false);
  const { user: currentUser, canAccess, isSuperAdmin } = useAuth();

  const [formData, setFormData] = useState({
    roles: [],
    assigned_categories: [],
    permission_overrides: {},
    profile_id: '',
    data_role_id: '',
    crm_email: '',
    hierarchy_type: 'sales_purchase',
  });

  const [addFormData, setAddFormData] = useState({ name: '', email: '', profile_id: '', data_role_id: '' });
  const [tempPasswordInfo, setTempPasswordInfo] = useState(null);
  const [overridesOpen, setOverridesOpen] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState(new Set());
  const [bulkProfileId, setBulkProfileId] = useState('');
  const [bulkDataRoleId, setBulkDataRoleId] = useState('');
  const [bulkSubmitting, setBulkSubmitting] = useState(false);
  const [orgNodes, setOrgNodes] = useState([]);

  const fetchOrgNodes = useCallback(async () => {
    try {
      const res = await userAPI.hierarchy();
      setOrgNodes(res.data.nodes || []);
    } catch {
      /* org chart is optional for non-admin viewers */
    }
  }, []);

  // Fetch static data once
  const fetchStaticData = useCallback(async () => {
    try {
      const [roleRes, profileRes, dataRoleRes, filterOptsRes] = await Promise.all([
        roleAPI.list(),
        profileAPI.list(),
        dataRoleAPI.list(),
        userAPI.filterOptions(),
      ]);
      setRoles(roleRes.data);
      setProfiles(profileRes.data.profiles || []);
      setDataRoles(dataRoleRes.data.data_roles || []);
      setZohoRoleOptions(filterOptsRes.data.zoho_roles || []);
    } catch (error) {
      toast.error('Failed to load data');
    }
  }, []);

  // Fetch users with pagination, search and every filter — all server-side (25 Oct 2026:
  // admin_level_only; 31 Oct 2026 Foundation F4: status/profile/data role/Zoho role/reports
  // to, so the count and paging always cover every matching user, not just the loaded page).
  const fetchSeq = useRef(0);
  const fetchUsers = useCallback(async () => {
    // Ignore out-of-order responses (e.g. a stale skip=50 request finishing after the
    // skip=0 one when a filter is toggled from page 2).
    const seq = ++fetchSeq.current;
    setLoading(true);
    try {
      const params = { skip: page * PAGE_SIZE, limit: PAGE_SIZE };
      if (debouncedSearch) params.search = debouncedSearch;
      if (adminLevelOnly) params.admin_level_only = true;
      if (statusFilter !== 'all') params.status = statusFilter;
      if (profileFilter !== 'all') params.profile_id = profileFilter;
      if (dataRoleFilter !== 'all') params.data_role_id = dataRoleFilter;
      if (zohoRoleFilter !== 'all') params.zoho_role = zohoRoleFilter;
      if (reportsToId !== 'all') {
        params.reports_to = reportsToId;
        if (allLevels) params.all_levels = true;
      }
      const res = await userAPI.list(params);
      if (seq !== fetchSeq.current) return;
      setUsers(res.data.users || []);
      setTotal(res.data.total || 0);
    } catch (error) {
      if (seq === fetchSeq.current) toast.error('Failed to load users');
    } finally {
      if (seq === fetchSeq.current) setLoading(false);
    }
  }, [page, debouncedSearch, adminLevelOnly, statusFilter, profileFilter, dataRoleFilter, zohoRoleFilter, reportsToId, allLevels]);

  useEffect(() => { fetchStaticData(); }, [fetchStaticData]);
  useEffect(() => { fetchOrgNodes(); }, [fetchOrgNodes]);
  // 31 Oct 2026 (Foundation F4 round, testing follow-up): one debounce for the typed
  // search text, one page-reset when any filter actually changes, one fetch whenever
  // fetchUsers' own inputs change — no more triple-fetch on load/filter change.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);
  useEffect(() => { setPage(0); }, [debouncedSearch, adminLevelOnly, statusFilter, profileFilter, dataRoleFilter, zohoRoleFilter, reportsToId, allLevels]);
  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  // Keep every filter in the URL so a view can be bookmarked/shared (31 Oct 2026,
  // Foundation F4 round — Users page filters).
  useEffect(() => {
    const next = {};
    if (search) next.q = search;
    if (adminLevelOnly) next.admin_only = '1';
    if (statusFilter !== 'all') next.status = statusFilter;
    if (profileFilter !== 'all') next.profile_id = profileFilter;
    if (dataRoleFilter !== 'all') next.data_role_id = dataRoleFilter;
    if (zohoRoleFilter !== 'all') next.zoho_role = zohoRoleFilter;
    if (reportsToId !== 'all') {
      next.reports_to = reportsToId;
      if (reportsToName) next.reports_to_name = reportsToName;
      if (allLevels) next.all_levels = '1';
    }
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, adminLevelOnly, statusFilter, profileFilter, dataRoleFilter, zohoRoleFilter, reportsToId, reportsToName, allLevels]);

  const hasActiveFilters = !!search || adminLevelOnly || statusFilter !== 'all' || profileFilter !== 'all'
    || dataRoleFilter !== 'all' || zohoRoleFilter !== 'all' || reportsToId !== 'all';

  const handleClearFilters = () => {
    setSearch(''); setAdminLevelOnly(false); setStatusFilter('all'); setProfileFilter('all');
    setDataRoleFilter('all'); setZohoRoleFilter('all'); setReportsToId('all'); setReportsToName('');
    setAllLevels(false); setMgrQuery(''); setMgrResults([]); setMgrPickerOpen(false);
  };

  const handleManagerSearch = async (q) => {
    setMgrQuery(q);
    if (q.length < 2) { setMgrResults([]); return; }
    try {
      const res = await userAPI.search(q);
      setMgrResults(res.data);
    } catch {
      setMgrResults([]);
    }
  };

  const handleSelectManager = (u) => {
    setReportsToId(u.id);
    setReportsToName(u.name || u.email);
    setMgrQuery(''); setMgrResults([]); setMgrPickerOpen(false);
  };

  const handleOpenModal = (user) => {
    setSelectedUser(user);
    setFormData({
      roles: user.roles || [],
      assigned_categories: user.assigned_categories || [],
      permission_overrides: user.permission_overrides || {},
      profile_id: user.profile_id || '',
      data_role_id: user.data_role_id || '',
      crm_email: user.crm_email || '',
      hierarchy_type: user.hierarchy_type || 'sales_purchase',
    });
    setOverridesOpen(false);
    setManualMgrQuery('');
    setManualMgrResults([]);
    setModalOpen(true);
  };

  const handleRoleToggle = (roleId) => {
    setFormData(prev => ({
      ...prev,
      roles: prev.roles.includes(roleId)
        ? prev.roles.filter(r => r !== roleId)
        : [...prev.roles, roleId],
    }));
  };

  const handleSubmit = async () => {
    if (!selectedUser) return;
    if (formData.roles.length === 0) {
      toast.error('User must have at least one role');
      return;
    }
    // 25 Oct 2026 (Security Round 2A, Task 3b): mirrors the backend's refusal to save a
    // user with no profile or no data role (PUT /users/{id}/roles).
    if (!formData.profile_id || !formData.data_role_id) {
      toast.error('Set a profile and data role first');
      return;
    }
    setSubmitting(true);
    try {
      await userAPI.updateRoles(selectedUser.id, formData);
      toast.success('User updated');
      setModalOpen(false);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleActive = async (user) => {
    try {
      await userAPI.toggleActive(user.id);
      toast.success(`User ${user.is_active ? 'disabled' : 'enabled'}`);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user status');
    }
  };

  const handleManualMgrSearch = async (q) => {
    setManualMgrQuery(q);
    if (q.length < 2) { setManualMgrResults([]); return; }
    try {
      const res = await userAPI.search(q);
      setManualMgrResults(res.data.filter((u) => u.id !== selectedUser?.id));
    } catch (error) {
      setManualMgrResults([]);
    }
  };

  const handleSetManualManager = async (managerId) => {
    setManualMgrSaving(true);
    try {
      const res = await userAPI.setManualManager(selectedUser.id, { manager_user_id: managerId });
      toast.success('Manager updated');
      setSelectedUser((p) => ({ ...p, reporting_to_user_id: res.data.reporting_to_user_id, reporting_to_name: res.data.reporting_to_name, manual_manager: !!res.data.reporting_to_user_id }));
      setManualMgrQuery(''); setManualMgrResults([]);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update manager');
    } finally {
      setManualMgrSaving(false);
    }
  };

  const handleAddUser = async () => {
    if (!addFormData.name || !addFormData.email) {
      toast.error('Name and email are required');
      return;
    }
    if (!addFormData.profile_id) {
      toast.error('Please select a profile');
      return;
    }
    if (!addFormData.data_role_id) {
      toast.error('Set a profile and data role first');
      return;
    }
    setSubmitting(true);
    try {
      // Derive a default role from the profile name for backward compatibility
      const selectedProf = profiles.find(p => p.id === addFormData.profile_id);
      const roleName = (selectedProf?.name || '').toLowerCase().replace(/[\s\/]+/g, '_');
      const matchedRole = roles.find(r => r.id === roleName || r.name.toLowerCase() === (selectedProf?.name || '').toLowerCase());
      const defaultRole = matchedRole ? matchedRole.id : 'viewer';

      const res = await userAPI.create({
        name: addFormData.name,
        email: addFormData.email,
        roles: [defaultRole],
        profile_id: addFormData.profile_id,
        data_role_id: addFormData.data_role_id,
      });
      toast.success(res.data.message || 'User created');
      if (res.data.temp_password && !res.data.invite_sent) {
        setTempPasswordInfo({ email: addFormData.email.trim().toLowerCase(), password: res.data.temp_password });
      }
      setAddModalOpen(false);
      setAddFormData({ name: '', email: '', profile_id: '', data_role_id: '' });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    try {
      await userAPI.delete(userId);
      toast.success('User deleted');
      setDeleteConfirm(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const isAdmin = canAccess('USERS', 'CREATE');
  const canEditUsers = canAccess('USERS', 'EDIT');
  const totalPages = Math.ceil(total / PAGE_SIZE);

  const toggleUserSelect = (id) => setSelectedUsers(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const selectAllOnPage = () => setSelectedUsers(new Set(displayedUsers.map(u => u.id)));
  const clearSelection = () => setSelectedUsers(new Set());

  const handleBulkUpdate = async () => {
    if (selectedUsers.size === 0) return;
    if (!bulkProfileId && !bulkDataRoleId) { toast.error('Select a Profile or Data Role to apply'); return; }
    setBulkSubmitting(true);
    try {
      const payload = { user_ids: Array.from(selectedUsers) };
      if (bulkProfileId) payload.profile_id = bulkProfileId;
      if (bulkDataRoleId) payload.data_role_id = bulkDataRoleId;
      const r = await userAPI.bulkUpdateRole(payload);
      toast.success(r.data.message);
      setSelectedUsers(new Set());
      setBulkProfileId('');
      setBulkDataRoleId('');
      fetchUsers();
    } catch (e) { toast.error(e.response?.data?.detail || 'Bulk update failed'); }
    finally { setBulkSubmitting(false); }
  };

  // Get selected profile's inherited permissions for override UI
  const selectedProfile = useMemo(() => {
    return profiles.find(p => p.id === formData.profile_id) || null;
  }, [profiles, formData.profile_id]);

  const inheritedPermissions = useMemo(() => {
    if (!selectedProfile) return [];
    const mp = selectedProfile.module_permissions || {};
    const perms = [];
    for (const [modId, actions] of Object.entries(mp)) {
      for (const action of actions) {
        perms.push({ module: modId, action, key: `${modId}:${action}` });
      }
    }
    return perms;
  }, [selectedProfile]);

  // Group inherited permissions by module for display
  const groupedPermissions = useMemo(() => {
    const groups = {};
    for (const p of inheritedPermissions) {
      if (!groups[p.module]) groups[p.module] = [];
      groups[p.module].push(p);
    }
    return groups;
  }, [inheritedPermissions]);

  const handleOverrideToggle = (key) => {
    setFormData(prev => {
      const overrides = { ...prev.permission_overrides };
      if (key in overrides) {
        // If already overridden, remove the override (revert to inherited)
        delete overrides[key];
      } else {
        // Deny this permission (override = false)
        overrides[key] = false;
      }
      return { ...prev, permission_overrides: overrides };
    });
  };

  const handleGrantOverride = (key) => {
    setFormData(prev => {
      const overrides = { ...prev.permission_overrides };
      if (overrides[key] === true) {
        delete overrides[key];
      } else {
        overrides[key] = true;
      }
      return { ...prev, permission_overrides: overrides };
    });
  };

  const overrideCount = Object.keys(formData.permission_overrides).filter(k => k.includes(':')).length;

  // 25 Oct 2026 (Security Round 2A follow-up): "Admin-level only" is now a server-side
  // filter (GET /users?admin_level_only=true) via fetchUsers, so `users` already is the
  // filtered, correctly-paginated/counted set — no client-side re-filtering needed.
  const displayedUsers = users;

  const renderUserListPanel = () => (
    <>
        <Card className="rounded-sm">
        <CardContent className="p-4 space-y-3">
          <div className="flex gap-4 items-center flex-wrap">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input placeholder="Search by name or email..." value={search} onChange={e => setSearch(e.target.value)} className="pl-10 rounded-sm" data-testid="users-search-input" />
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer select-none whitespace-nowrap" data-testid="admin-level-only-filter">
              <Checkbox checked={adminLevelOnly} onCheckedChange={(c) => setAdminLevelOnly(!!c)} />
              Admin-level only
            </label>
            {canEditUsers && (
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={selectAllOnPage} className="rounded-sm">Select Page</Button>
                {selectedUsers.size > 0 && <Button variant="ghost" size="sm" onClick={clearSelection} className="rounded-sm">Clear ({selectedUsers.size})</Button>}
              </div>
            )}
          </div>

          <div className="flex gap-3 items-center flex-wrap">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-36 h-9 rounded-sm text-sm" data-testid="users-filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Status: All</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>

            <Select value={profileFilter} onValueChange={setProfileFilter}>
              <SelectTrigger className="w-48 h-9 rounded-sm text-sm" data-testid="users-filter-profile"><SelectValue placeholder="Profile" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Profile: All</SelectItem>
                <SelectItem value="none">No profile</SelectItem>
                {profiles.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>

            <Select value={dataRoleFilter} onValueChange={setDataRoleFilter}>
              <SelectTrigger className="w-48 h-9 rounded-sm text-sm" data-testid="users-filter-data-role"><SelectValue placeholder="Data role" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Data role: All</SelectItem>
                {dataRoles.map(r => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
              </SelectContent>
            </Select>

            <Select value={zohoRoleFilter} onValueChange={setZohoRoleFilter}>
              <SelectTrigger className="w-48 h-9 rounded-sm text-sm" data-testid="users-filter-zoho-role"><SelectValue placeholder="Zoho role" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Zoho role: All</SelectItem>
                {zohoRoleOptions.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
              </SelectContent>
            </Select>

            <div className="relative">
              {reportsToId === 'all' ? (
                <div className="relative">
                  <Input
                    placeholder="Reports to..."
                    value={mgrQuery}
                    onChange={e => handleManagerSearch(e.target.value)}
                    onFocus={() => setMgrPickerOpen(true)}
                    className="w-44 h-9 rounded-sm text-sm"
                    data-testid="users-filter-reports-to-input"
                  />
                  {mgrPickerOpen && mgrResults.length > 0 && (
                    <div className="absolute z-20 mt-1 w-56 bg-popover border rounded-sm shadow-md max-h-48 overflow-auto">
                      {mgrResults.map(u => (
                        <button
                          key={u.id}
                          type="button"
                          onClick={() => handleSelectManager(u)}
                          className="w-full text-left px-3 py-1.5 text-sm hover:bg-muted"
                          data-testid={`users-filter-reports-to-option-${u.id}`}
                        >
                          {u.name || u.email}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <Badge variant="secondary" className="h-9 px-3 flex items-center gap-2 rounded-sm" data-testid="users-filter-reports-to-chip">
                  Reports to: {reportsToName || reportsToId}
                  <button type="button" onClick={() => { setReportsToId('all'); setReportsToName(''); setAllLevels(false); }} data-testid="users-filter-reports-to-clear">
                    <XIcon className="h-3 w-3" />
                  </button>
                </Badge>
              )}
            </div>

            {reportsToId !== 'all' && (
              <label className="flex items-center gap-2 text-sm cursor-pointer select-none whitespace-nowrap" data-testid="users-filter-all-levels">
                <Checkbox checked={allLevels} onCheckedChange={(c) => setAllLevels(!!c)} />
                Include all levels
              </label>
            )}

            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={handleClearFilters} className="rounded-sm" data-testid="users-filter-clear">
                Clear filters
              </Button>
            )}

            <span className="text-sm text-muted-foreground ml-auto" data-testid="users-filter-count">
              {loading ? 'Loading…' : `${total} user${total === 1 ? '' : 's'}`}
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Bulk Action Bar */}
      {canEditUsers && selectedUsers.size > 0 && (
        <Card className="rounded-sm border-primary/30 bg-primary/5">
          <CardContent className="p-3 flex items-center gap-3 flex-wrap">
            <span className="text-sm font-medium">{selectedUsers.size} user{selectedUsers.size > 1 ? 's' : ''} selected</span>
            <Select value={bulkProfileId || '_none'} onValueChange={v => setBulkProfileId(v === '_none' ? '' : v)}>
              <SelectTrigger className="w-48 h-8 rounded-sm text-sm" data-testid="bulk-profile-select"><SelectValue placeholder="Set Profile..." /></SelectTrigger>
              <SelectContent>
                <SelectItem value="_none">— Select Profile —</SelectItem>
                {profiles.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={bulkDataRoleId || '_none'} onValueChange={v => setBulkDataRoleId(v === '_none' ? '' : v)}>
              <SelectTrigger className="w-48 h-8 rounded-sm text-sm" data-testid="bulk-datarole-select"><SelectValue placeholder="Set Data Role..." /></SelectTrigger>
              <SelectContent>
                <SelectItem value="_none">— Select Data Role —</SelectItem>
                {dataRoles.map(r => <SelectItem key={r.id} value={r.id}>{r.name} ({r.scope})</SelectItem>)}
              </SelectContent>
            </Select>
            <Button size="sm" onClick={handleBulkUpdate} disabled={bulkSubmitting || (!bulkProfileId && !bulkDataRoleId)} className="rounded-sm" data-testid="bulk-apply-btn">
              {bulkSubmitting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
              Apply to {selectedUsers.size} user{selectedUsers.size > 1 ? 's' : ''}
            </Button>
          </CardContent>
        </Card>
      )}

      <Card className="rounded-sm">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50">
                {canEditUsers && <TableHead className="w-10"><Checkbox checked={selectedUsers.size === displayedUsers.length && displayedUsers.length > 0} onCheckedChange={(c) => c ? setSelectedUsers(new Set(displayedUsers.map(u => u.id))) : clearSelection()} /></TableHead>}
                <TableHead className="font-medium text-xs uppercase">Name</TableHead>
                <TableHead className="font-medium text-xs uppercase">Email</TableHead>
                <TableHead className="font-medium text-xs uppercase">Zoho Role</TableHead>
                <TableHead className="font-medium text-xs uppercase">Profile</TableHead>
                <TableHead className="font-medium text-xs uppercase">Data Role</TableHead>
                <TableHead className="font-medium text-xs uppercase">Reports To</TableHead>
                <TableHead className="font-medium text-xs uppercase">Employee ID</TableHead>
                <TableHead className="font-medium text-xs uppercase">Status</TableHead>
                <TableHead className="font-medium text-xs uppercase">Created</TableHead>
                <TableHead className="font-medium text-xs uppercase text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={canEditUsers ? 11 : 10} className="text-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin mx-auto text-muted-foreground" />
                  </TableCell>
                </TableRow>
              ) : displayedUsers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={canEditUsers ? 11 : 10} className="text-center py-8">
                    <Users className="h-12 w-12 mx-auto text-muted-foreground/50 mb-2" />
                    <p className="text-muted-foreground">{adminLevelOnly ? 'No admin-level users found' : 'No users found'}</p>
                  </TableCell>
                </TableRow>
              ) : (
                displayedUsers.map((user) => (
                  <TableRow key={user.id} className={`table-row-hover ${selectedUsers.has(user.id) ? 'bg-primary/5' : ''}`} data-testid={`user-row-${user.email}`}>
                    {canEditUsers && <TableCell className="w-10"><Checkbox checked={selectedUsers.has(user.id)} onCheckedChange={() => toggleUserSelect(user.id)} /></TableCell>}
                    <TableCell className="font-medium">
                      <span className="flex items-center gap-1.5">
                        {user.name}
                        {user.hierarchy_type === 'product' && (
                          <Badge variant="outline" className="rounded-sm text-[10px] font-normal bg-amber-50 text-amber-800 border-amber-200" data-testid={`user-hierarchy-badge-${user.email}`}>
                            Product
                          </Badge>
                        )}
                        {isUserAdminLevel(user) && (
                          <Badge variant="outline" className="rounded-sm text-[10px] font-normal bg-red-50 text-red-800 border-red-200" data-testid={`user-admin-level-badge-${user.email}`}>
                            Admin-level
                          </Badge>
                        )}
                      </span>
                    </TableCell>
                    <TableCell className="text-sm">{user.email}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{user.crm_role_name || '—'}</TableCell>
                    <TableCell>
                      {user.profile_name && user.profile_name !== 'Not set' ? (
                        <Badge variant="outline" className="rounded-sm text-xs">
                          <Shield className="h-3 w-3 mr-1" />
                          {user.profile_name}
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="rounded-sm text-xs bg-amber-50 text-amber-800 border-amber-200" data-testid={`user-profile-not-set-${user.email}`}>
                          Not set
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {user.data_role_name && user.data_role_name !== 'Not set' ? (
                        <Badge variant="secondary" className="rounded-sm text-xs">
                          {user.data_role_name}
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="rounded-sm text-xs bg-amber-50 text-amber-800 border-amber-200" data-testid={`user-data-role-not-set-${user.email}`}>
                          Not set &middot; {user.data_scope}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {user.reporting_to_name ? (
                        <span className="text-sm">{user.reporting_to_name}</span>
                      ) : (
                        <span className="text-xs text-muted-foreground">— Top Level</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <StaffNumberCell user={user} editable={isSuperAdmin} onSaved={fetchUsers} />
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline"
                        className={user.is_active ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-100 text-gray-600'}>
                        {user.is_active ? 'Active' : 'Disabled'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {formatDateTime(user.created_at)}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        {canEditUsers && (
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0"
                            onClick={() => handleOpenModal(user)}
                            data-testid={`edit-user-${user.email}`}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                        )}
                        {canEditUsers && user.id !== currentUser?.id && (
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0"
                            onClick={() => handleToggleActive(user)}
                            data-testid={`toggle-user-${user.email}`}>
                            {user.is_active ? (
                              <UserX className="h-4 w-4 text-destructive" />
                            ) : (
                              <UserCheck className="h-4 w-4 text-emerald-600" />
                            )}
                          </Button>
                        )}
                        {isAdmin && user.id !== currentUser?.id && (
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0"
                            onClick={async () => {
                              const pw = prompt(`Reset password for ${user.name}?\nEnter new password (min 6 chars):`);
                              if (pw && pw.length >= 6) {
                                try { await userAPI.resetPassword(user.id, { new_password: pw }); toast.success(`Password reset for ${user.email}`); } catch(e) { toast.error(e?.response?.data?.detail || 'Failed'); }
                              } else if (pw) { toast.error('Password must be at least 6 characters'); }
                            }}
                            data-testid={`reset-pw-${user.email}`} title="Reset Password">
                            <KeyRound className="h-4 w-4 text-amber-600" />
                          </Button>
                        )}
                        {isSuperAdmin && (
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0"
                            onClick={async () => {
                              try {
                                const res = await userAPI.unlockLogin(user.id);
                                toast.success(res.data?.message || `Login unlocked for ${user.email}`);
                              } catch (e) { toast.error(e.response?.data?.detail || 'Failed to unlock'); }
                            }}
                            data-testid={`unlock-login-${user.email}`} title="Unlock Login (clears failed-login lockouts)">
                            <LockOpen className="h-4 w-4 text-sky-600" />
                          </Button>
                        )}
                        {isAdmin && user.id !== currentUser?.id && (
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0"
                            onClick={() => setDeleteConfirm(user)}
                            data-testid={`delete-user-${user.email}`}>
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Pagination */}
      {total > PAGE_SIZE && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            {adminLevelOnly
              ? `Showing ${page * PAGE_SIZE + 1}–${Math.min((page + 1) * PAGE_SIZE, total)} of ${total} admin-level users`
              : `Showing ${page * PAGE_SIZE + 1}–${Math.min((page + 1) * PAGE_SIZE, total)} of ${total} users`}
          </p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0} className="rounded-sm">Previous</Button>
            <span className="flex items-center px-3 text-sm">Page {page + 1} of {totalPages || 1}</span>
            <Button variant="outline" size="sm" onClick={() => setPage(p => p + 1)} disabled={page >= totalPages - 1} className="rounded-sm">Next</Button>
          </div>
        </div>
      )}
    </>
  );

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Users</h1>
          <p className="text-muted-foreground text-sm">Manage user accounts, profiles, and data roles</p>
        </div>
        {isAdmin && (
          <Button onClick={() => setAddModalOpen(true)} className="rounded-sm" data-testid="add-user-btn">
            <Plus className="h-4 w-4 mr-2" />
            Add User
          </Button>
        )}
      </div>

      <Tabs defaultValue="list" className="space-y-4">
        <TabsList>
          <TabsTrigger value="list" data-testid="users-tab-list">User List</TabsTrigger>
          <TabsTrigger value="orgchart" data-testid="users-tab-orgchart">Org Chart</TabsTrigger>
          <TabsTrigger value="product" data-testid="users-tab-product-hierarchy">Product Hierarchy</TabsTrigger>
          {isAdmin && <TabsTrigger value="drift" data-testid="users-tab-drift">Hierarchy Drift</TabsTrigger>}
        </TabsList>

        <TabsContent value="list" className="space-y-4">
          {renderUserListPanel()}
        </TabsContent>

        <TabsContent value="orgchart" className="space-y-4">
          <HierarchySyncPanel onSynced={() => { fetchOrgNodes(); fetchUsers(); }} />
          <Card className="rounded-sm">
            <CardContent className="p-6">
              <OrgChartView nodes={orgNodes} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="product">
          <ProductHierarchyTab allUsers={orgNodes} onChanged={() => { fetchOrgNodes(); fetchUsers(); }} />
        </TabsContent>

        <TabsContent value="drift">
          <HierarchyDriftPanel onApplied={() => { fetchOrgNodes(); fetchUsers(); }} />
        </TabsContent>
      </Tabs>

      {/* Edit User Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-primary" />
              Edit User: {selectedUser?.name}
            </DialogTitle>
          </DialogHeader>
          <ScrollArea className="max-h-[65vh]">
            <div className="space-y-5 pr-4">
              {/* User Info Summary */}
              <div className="p-3 bg-muted rounded-sm">
                <p className="text-sm font-medium">{selectedUser?.name}</p>
                <p className="text-xs text-muted-foreground">{selectedUser?.email}</p>
              </div>

              {/* Profile Assignment */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">Profile (Actions)</Label>
                <p className="text-xs text-muted-foreground">Determines what actions this user can perform on each module.</p>
                <Select
                  value={formData.profile_id || '_none'}
                  onValueChange={v => {
                    const newProfileId = v === '_none' ? '' : v;
                    const mappedRoleId = PROFILE_TO_LEGACY_ROLE[newProfileId];
                    const availableRoleIds = roles.map(r => r.id);
                    setFormData(p => {
                      // Keep profile_id and the legacy `roles` list in agreement (25 Oct 2026,
                      // Security Round 2A, Task 3c): every profile change now re-derives roles
                      // from PROFILE_TO_LEGACY_ROLE, not just for Super Admin/Admin — a PM-profile
                      // user who used to be "buyer" no longer keeps the stale "buyer" legacy role.
                      let newRoles;
                      if (mappedRoleId && availableRoleIds.includes(mappedRoleId)) {
                        newRoles = [mappedRoleId];
                      } else {
                        newRoles = p.roles.filter(r => r !== 'super_admin' && r !== 'admin');
                      }
                      return { ...p, profile_id: newProfileId, roles: newRoles, permission_overrides: {} };
                    });
                  }}
                >
                  <SelectTrigger className="rounded-sm" data-testid="user-profile-select">
                    <SelectValue placeholder="Select a profile..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none">No Profile</SelectItem>
                    {profiles.map(p => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.name} {p.is_system ? '(System)' : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Permission Overrides - only show when a profile is selected */}
              {selectedProfile && (
                <div className="space-y-2">
                  <button
                    type="button"
                    onClick={() => setOverridesOpen(!overridesOpen)}
                    className="flex items-center gap-2 text-sm font-medium hover:text-primary transition-colors w-full"
                    data-testid="toggle-overrides-btn"
                  >
                    {overridesOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                    Permission Overrides
                    {overrideCount > 0 && (
                      <Badge variant="destructive" className="rounded-sm text-xs ml-auto">
                        {overrideCount} override{overrideCount !== 1 ? 's' : ''}
                      </Badge>
                    )}
                  </button>
                  <p className="text-xs text-muted-foreground">
                    Inherited from <strong>{selectedProfile.name}</strong>. Click to deny or grant individual permissions.
                  </p>

                  {overridesOpen && (
                    <div className="border rounded-sm max-h-[250px] overflow-y-auto" data-testid="overrides-panel">
                      {Object.entries(groupedPermissions).map(([modId, perms]) => (
                        <div key={modId} className="border-b last:border-b-0">
                          <div className="px-3 py-1.5 bg-muted/30">
                            <span className="text-xs font-semibold text-muted-foreground uppercase">{modId}</span>
                          </div>
                          {perms.map(perm => {
                            const override = formData.permission_overrides[perm.key];
                            const isDenied = override === false;
                            return (
                              <div key={perm.key}
                                className={`flex items-center justify-between px-3 py-1.5 text-sm hover:bg-muted/20 transition-colors ${isDenied ? 'bg-red-50' : ''}`}
                                data-testid={`override-${perm.key}`}
                              >
                                <span className={`${isDenied ? 'line-through text-muted-foreground' : ''}`}>
                                  {perm.action}
                                </span>
                                <div className="flex items-center gap-1">
                                  {isDenied ? (
                                    <Badge variant="destructive" className="text-xs rounded-sm cursor-pointer"
                                      onClick={() => handleOverrideToggle(perm.key)}
                                      data-testid={`override-denied-${perm.key}`}>
                                      <XIcon className="h-3 w-3 mr-1" />Denied
                                    </Badge>
                                  ) : (
                                    <>
                                      <Badge variant="outline" className="text-xs rounded-sm bg-emerald-50 text-emerald-700">
                                        <Check className="h-3 w-3 mr-1" />Inherited
                                      </Badge>
                                      <Button variant="ghost" size="sm" className="h-6 px-2 text-xs text-destructive hover:text-destructive"
                                        onClick={() => handleOverrideToggle(perm.key)}
                                        data-testid={`deny-btn-${perm.key}`}>
                                        Deny
                                      </Button>
                                    </>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ))}

                      {/* Grant additional permissions not in profile */}
                      <div className="border-t">
                        <div className="px-3 py-1.5 bg-blue-50/50">
                          <span className="text-xs font-semibold text-blue-700 uppercase">Grant Additional</span>
                        </div>
                        <p className="px-3 py-1 text-xs text-muted-foreground">
                          Grant permissions not included in the profile.
                        </p>
                        {(() => {
                          const ACTIONS = ['VIEW', 'CREATE', 'EDIT', 'DELETE', 'APPROVE', 'IMPORT', 'EXPORT'];
                          const allModules = [...new Set([
                            ...Object.keys(selectedProfile.module_permissions || {}),
                            'DASHBOARD', 'ITEMS', 'TARGETS', 'OFFERS', 'PURCHASES', 'APPROVALS', 'REPORTS',
                            'PERFORMANCE', 'AI_ASSISTANT', 'AI_ENGINE', 'IMPORT_EXPORT', 'ZOHO_SYNC',
                            'DIRECTORY', 'CATEGORIES', 'PMS', 'USERS', 'ROLES_PROFILES', 'SETTINGS',
                            'AUDIT_LOG', 'RECONCILE', 'AI_METRICS'
                          ])];
                          const extraPerms = [];
                          for (const mod of allModules) {
                            const profileActions = (selectedProfile.module_permissions || {})[mod] || [];
                            for (const action of ACTIONS) {
                              if (!profileActions.includes(action)) {
                                const key = `${mod}:${action}`;
                                extraPerms.push({ module: mod, action, key });
                              }
                            }
                          }
                          const grantedExtras = extraPerms.filter(p => formData.permission_overrides[p.key] === true);
                          const ungrantedExtras = extraPerms.filter(p => formData.permission_overrides[p.key] !== true);

                          return (
                            <>
                              {grantedExtras.map(perm => (
                                <div key={perm.key} className="flex items-center justify-between px-3 py-1.5 text-sm bg-blue-50 hover:bg-blue-100/50">
                                  <span className="text-blue-800">{perm.module}: {perm.action}</span>
                                  <Badge variant="outline" className="text-xs rounded-sm bg-blue-100 text-blue-800 cursor-pointer"
                                    onClick={() => handleGrantOverride(perm.key)}
                                    data-testid={`granted-extra-${perm.key}`}>
                                    <Check className="h-3 w-3 mr-1" />Granted (click to remove)
                                  </Badge>
                                </div>
                              ))}
                              {ungrantedExtras.length > 0 && (
                                <details className="px-3 py-1">
                                  <summary className="text-xs text-muted-foreground cursor-pointer hover:text-foreground">
                                    {ungrantedExtras.length} available permissions to grant...
                                  </summary>
                                  <div className="mt-1 max-h-[150px] overflow-y-auto">
                                    {ungrantedExtras.map(perm => (
                                      <div key={perm.key} className="flex items-center justify-between py-1 text-xs hover:bg-muted/20">
                                        <span className="text-muted-foreground">{perm.module}: {perm.action}</span>
                                        <Button variant="ghost" size="sm" className="h-5 px-2 text-xs text-blue-600"
                                          onClick={() => handleGrantOverride(perm.key)}
                                          data-testid={`grant-btn-${perm.key}`}>
                                          Grant
                                        </Button>
                                      </div>
                                    ))}
                                  </div>
                                </details>
                              )}
                            </>
                          );
                        })()}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Data Role Assignment */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">Data Role (Visibility)</Label>
                <p className="text-xs text-muted-foreground">Determines what data this user can see.</p>
                <Select
                  value={formData.data_role_id || '_none'}
                  onValueChange={v => setFormData(p => ({ ...p, data_role_id: v === '_none' ? '' : v }))}
                >
                  <SelectTrigger className="rounded-sm" data-testid="user-data-role-select">
                    <SelectValue placeholder="Select a data role..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none">No Data Role (Default)</SelectItem>
                    {dataRoles.map(r => (
                      <SelectItem key={r.id} value={r.id}>
                        {r.name} ({r.scope})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {formData.data_role_id === 'org_all' && (
                  <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-sm px-2 py-1.5" data-testid="org-all-data-role-note">
                    Full access to all business data. For complete-access users only.
                  </p>
                )}
              </div>

              {/* Reports To (Manager) — CRM-only now, read-only here */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">Reports To (Manager)</Label>
                <div className="rounded-sm border bg-muted/40 px-3 py-2 text-sm" data-testid="user-manager-readonly">
                  {selectedUser?.reporting_to_name || <span className="text-muted-foreground">No Manager (Top Level)</span>}
                  {selectedUser?.hierarchy_gap_reason && (
                    <Badge variant="outline" className="ml-2 rounded-sm text-[10px] font-normal bg-amber-50 text-amber-800 border-amber-200">
                      {selectedUser.hierarchy_gap_reason}
                    </Badge>
                  )}
                  {selectedUser?.manual_manager && (
                    <Badge variant="outline" className="ml-2 rounded-sm text-[10px] font-normal bg-blue-50 text-blue-800 border-blue-200">
                      Manual
                    </Badge>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  {selectedUser?.has_crm_match
                    ? 'Managed in Zoho CRM — change it there. TradeIQ updates daily.'
                    : 'No Zoho CRM record for this user — set the manager manually below.'}
                </p>
              </div>

              {/* Manual manager — Super Admin only, and only for users with no Zoho CRM match at all */}
              {isSuperAdmin && selectedUser && !selectedUser.has_crm_match && (
                <div className="space-y-2" data-testid="manual-manager-section">
                  <Label className="text-sm font-medium">Manager (not in Zoho CRM)</Label>
                  <p className="text-xs text-muted-foreground">
                    This person has no Zoho CRM record, so CRM can't set their manager. Set it
                    manually here — if they ever get a real CRM match, CRM takes over automatically.
                  </p>
                  <Input
                    placeholder="Search active users by name or email..."
                    value={manualMgrQuery}
                    onChange={(e) => handleManualMgrSearch(e.target.value)}
                    data-testid="manual-manager-search-input"
                  />
                  {manualMgrResults.length > 0 && (
                    <div className="rounded-sm border divide-y max-h-40 overflow-y-auto">
                      {manualMgrResults.map((u) => (
                        <button
                          key={u.id}
                          type="button"
                          disabled={manualMgrSaving}
                          className="w-full text-left px-3 py-2 text-sm hover:bg-muted/60"
                          data-testid={`manual-manager-option-${u.id}`}
                          onClick={() => handleSetManualManager(u.id)}
                        >
                          {u.name} <span className="text-muted-foreground">({u.email})</span>
                        </button>
                      ))}
                    </div>
                  )}
                  {selectedUser.reporting_to_user_id && (
                    <Button
                      variant="outline" size="sm" disabled={manualMgrSaving}
                      data-testid="manual-manager-clear-btn"
                      onClick={() => handleSetManualManager('')}
                    >
                      Clear Manager
                    </Button>
                  )}
                </div>
              )}

              {/* CRM Email override — used when this account's email doesn't match its CRM record */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">CRM Email (override)</Label>
                <p className="text-xs text-muted-foreground">
                  Only set this if the Zoho CRM record for this person uses a different email
                  address. Leave blank to match on this user's own email.
                </p>
                <Input
                  value={formData.crm_email}
                  onChange={e => setFormData(p => ({ ...p, crm_email: e.target.value }))}
                  placeholder="e.g. aaron@vstl.hk"
                  className="rounded-sm"
                  data-testid="user-crm-email-input"
                />
              </div>

              {/* Hierarchy */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">Hierarchy</Label>
                <p className="text-xs text-muted-foreground">
                  Sales/Purchase = sees own records plus all direct &amp; indirect reports' records.
                  Product = sees all data for assigned SKUs (and their product reports' SKUs). One hierarchy per person.
                </p>
                <Select
                  value={formData.hierarchy_type || 'sales_purchase'}
                  onValueChange={v => setFormData(p => ({ ...p, hierarchy_type: v }))}
                >
                  <SelectTrigger className="rounded-sm" data-testid="user-hierarchy-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sales_purchase">Sales / Purchase</SelectItem>
                    <SelectItem value="product">Product</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Legacy Roles */}
              <div className="space-y-3">
                <Label className="text-sm font-medium">Legacy Roles</Label>
                <p className="text-xs text-muted-foreground">Used as fallback when no Profile is assigned.</p>
                <div className="space-y-2 border rounded-sm p-3">
                  {roles.map((role) => {
                    const isChecked = formData.roles.includes(role.id);
                    return (
                      <div key={role.id} className={`flex items-center justify-between p-2 rounded-sm hover:bg-muted/50 ${isChecked ? 'bg-primary/5 border border-primary/20' : ''}`}>
                        <div className="flex items-center space-x-3">
                          <Checkbox
                            id={`role-${role.id}`}
                            checked={isChecked}
                            onCheckedChange={() => handleRoleToggle(role.id)}
                            data-testid={`role-toggle-${role.id}`}
                          />
                          <div>
                            <label htmlFor={`role-${role.id}`} className="text-sm font-medium cursor-pointer">
                              {role.name}
                            </label>
                            <p className="text-xs text-muted-foreground">{role.description || ''}</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                {formData.roles.length === 0 && (
                  <p className="text-xs text-destructive">User must have at least one role</p>
                )}
              </div>

            </div>
          </ScrollArea>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button type="button" variant="outline" onClick={() => setModalOpen(false)} className="rounded-sm">
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={submitting || formData.roles.length === 0 || !formData.profile_id || !formData.data_role_id} className="rounded-sm" data-testid="save-user-btn">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add User Modal */}
      <Dialog open={addModalOpen} onOpenChange={setAddModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add New User</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-sm font-medium">Name *</Label>
              <Input value={addFormData.name} onChange={e => setAddFormData(prev => ({ ...prev, name: e.target.value }))} placeholder="Full name" data-testid="add-user-name" />
            </div>
            <div>
              <Label className="text-sm font-medium">Email *</Label>
              <Input type="email" value={addFormData.email} onChange={e => setAddFormData(prev => ({ ...prev, email: e.target.value }))} placeholder="user@example.com" data-testid="add-user-email" />
            </div>
            <div>
              <Label className="text-sm font-medium">Profile *</Label>
              <p className="text-xs text-muted-foreground">Determines module-level permissions.</p>
              <Select value={addFormData.profile_id || '_none'} onValueChange={v => setAddFormData(prev => ({ ...prev, profile_id: v === '_none' ? '' : v }))}>
                <SelectTrigger className="rounded-sm mt-1" data-testid="add-user-profile"><SelectValue placeholder="Select a profile..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none" disabled>Select a profile...</SelectItem>
                  {profiles.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
              {!addFormData.profile_id && <p className="text-xs text-destructive mt-1">Please select a profile.</p>}
            </div>
            <div>
              <Label className="text-sm font-medium">Data Role *</Label>
              <p className="text-xs text-muted-foreground">Determines data visibility scope.</p>
              <Select value={addFormData.data_role_id || '_none'} onValueChange={v => setAddFormData(prev => ({ ...prev, data_role_id: v === '_none' ? '' : v }))}>
                <SelectTrigger className="rounded-sm mt-1" data-testid="add-user-data-role"><SelectValue placeholder="Select a data role..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none" disabled>Select a data role...</SelectItem>
                  {dataRoles.map(r => <SelectItem key={r.id} value={r.id}>{r.name} ({r.scope})</SelectItem>)}
                </SelectContent>
              </Select>
              {!addFormData.data_role_id && <p className="text-xs text-destructive mt-1">Set a profile and data role first.</p>}
              {addFormData.data_role_id === 'org_all' && (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-sm px-2 py-1.5 mt-1" data-testid="add-user-org-all-note">
                  Full access to all business data. For complete-access users only.
                </p>
              )}
            </div>
            <p className="text-xs text-muted-foreground">An invite email with temporary password will be sent to the user.</p>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setAddModalOpen(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={handleAddUser} disabled={submitting || !addFormData.profile_id || !addFormData.data_role_id} className="rounded-sm" data-testid="submit-add-user">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create & Send Invite'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete User</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Are you sure you want to delete <strong>{deleteConfirm?.name}</strong> ({deleteConfirm?.email})? This action cannot be undone.
          </p>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setDeleteConfirm(null)} className="rounded-sm">Cancel</Button>
            <Button variant="destructive" onClick={() => handleDeleteUser(deleteConfirm.id)} className="rounded-sm" data-testid="confirm-delete-user">
              Delete User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Temp Password Dialog — shown once, copy button, never shown again */}
      <Dialog open={!!tempPasswordInfo} onOpenChange={(v) => { if (!v) setTempPasswordInfo(null); }}>
        <DialogContent className="sm:max-w-sm" data-testid="temp-password-dialog">
          <DialogHeader>
            <DialogTitle>User Created</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Email (not configured) couldn't be sent — share this temporary password with <strong>{tempPasswordInfo?.email}</strong> yourself.
          </p>
          <div className="flex items-center gap-2 p-2.5 border rounded-sm bg-muted">
            <span className="font-mono text-sm flex-1 truncate" data-testid="temp-password-value">{tempPasswordInfo?.password}</span>
            <Button
              size="sm"
              variant="outline"
              className="rounded-sm shrink-0"
              data-testid="copy-temp-password-btn"
              onClick={() => {
                navigator.clipboard.writeText(tempPasswordInfo?.password || '');
                toast.success('Copied to clipboard');
              }}
            >
              Copy
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">Share this securely. It will not be shown again.</p>
          <DialogFooter>
            <Button onClick={() => setTempPasswordInfo(null)} className="rounded-sm w-full" data-testid="close-temp-password-dialog">Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Employee ID inline-editable cell (HR round, 27 Oct 2026) — Super Admin only here; HR
// edits the same field from Conduct > Employee IDs (components/conduct/EmployeeIdsTab).
function StaffNumberCell({ user, editable, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(user.staff_number || '');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await userAPI.setStaffNumber(user.id, { staff_number: value });
      toast.success('Employee ID updated');
      setEditing(false);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update employee ID');
    } finally {
      setSaving(false);
    }
  };

  if (!editable) return <span className="text-sm">{user.staff_number || '—'}</span>;
  if (!editing) {
    return (
      <button type="button" className="text-sm hover:underline" data-testid={`staff-number-cell-${user.email}`} onClick={() => setEditing(true)}>
        {user.staff_number || <span className="text-amber-700 text-xs">Set ID</span>}
      </button>
    );
  }
  return (
    <div className="flex items-center gap-1">
      <Input className="h-7 w-28 text-xs" value={value} onChange={(e) => setValue(e.target.value)}
        data-testid={`staff-number-input-${user.email}`} autoFocus />
      <Button size="sm" variant="ghost" className="h-7 px-2" disabled={saving} onClick={save} data-testid={`staff-number-save-${user.email}`}>
        {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Save'}
      </Button>
    </div>
  );
}